import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import {
  ReportingComparisonScopes,
  type ReportingComparisonScope,
} from 'ag-common-lib/lib/contracts/reporting-api.contracts';

/**
 * Why a report page has nothing to show. `no-data`: the viewer may see the report, but no
 * agencies are linked to them for it (the API answers 404). `no-access`: the report isn't
 * theirs (403).
 */
export type ReportUnavailableReason = 'no-data' | 'no-access';

/** The reason behind a reporting API error, or null for failures that aren't about access. */
export function getReportUnavailableReason(error: unknown): ReportUnavailableReason | null {
  if (!(error instanceof HttpErrorResponse)) {
    return null;
  }

  if (error.status === 404) {
    return 'no-data';
  }

  return error.status === 403 ? 'no-access' : null;
}

const NO_DATA_TEXTS: Record<ReportingComparisonScope, string> = {
  [ReportingComparisonScopes.rmd]: 'No production to display: no agencies are linked to you as an RMD yet.',
  [ReportingComparisonScopes.mga]: "No production to display: your agency isn't linked to your account yet.",
  [ReportingComparisonScopes.allianceGroup]: 'No production to display at this time.',
};

/**
 * A report page's "nothing to show" state, in place of the report and its settings, styled like
 * the dashboards' "No Paid Production" state. The page's own header keeps the way back.
 */
@Component({
  selector: 'ag-shr-report-unavailable',
  standalone: true,
  templateUrl: './report-unavailable.component.html',
  styleUrls: ['./report-unavailable.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReportUnavailableComponent {
  readonly reason = input.required<ReportUnavailableReason>();
  readonly scope = input<ReportingComparisonScope | null>(null);

  protected readonly textSig = computed(() =>
    this.reason() === 'no-data'
      ? NO_DATA_TEXTS[this.scope() ?? ReportingComparisonScopes.allianceGroup]
      : "This report isn't available for your account.",
  );
}
